import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// Tema da plataforma. A PREFERÊNCIA é 'light', 'dark' ou 'sistema'; o TEMA
// aplicado (data-theme no <html>) é sempre 'light' ou 'dark'.
// - 'light'/'dark' ficam gravados em localStorage[exxerTheme].
// - 'sistema' = chave apagada: o tema acompanha o prefers-color-scheme, inclusive
//   quando o sistema operacional troca com a página aberta.
// A tela Meu perfil oferece as três opções; o menu da engrenagem e as telas de
// acesso só alternam claro/escuro (toggleTheme), o que grava a escolha.
export const STORAGE_KEY = 'exxerTheme';
export const PREFERENCIAS_TEMA = Object.freeze(['light', 'dark', 'sistema']);
const ThemeContext = createContext(null);

const lerPreferencia = () => {
  if (typeof window === 'undefined') return 'light';
  try {
    const gravado = window.localStorage.getItem(STORAGE_KEY);
    if (gravado === 'light' || gravado === 'dark') return gravado;
  } catch (_err) { /* sem storage: segue o sistema */ }
  return 'sistema';
};

const consultaEscuro = () => (typeof window === 'undefined' ? null : window.matchMedia?.('(prefers-color-scheme: dark)') || null);
const temaDoSistema = () => (consultaEscuro()?.matches ? 'dark' : 'light');

export const ThemeProvider = ({ children }) => {
  const [preferencia, setPreferenciaEstado] = useState(lerPreferencia);
  const [sistema, setSistema] = useState(temaDoSistema);
  const theme = preferencia === 'sistema' ? sistema : preferencia;

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = theme;
    root.style.colorScheme = theme;
  }, [theme]);

  useEffect(() => {
    try {
      if (preferencia === 'sistema') window.localStorage.removeItem(STORAGE_KEY);
      else window.localStorage.setItem(STORAGE_KEY, preferencia);
    } catch (_err) { /* sem storage: vale só nesta página */ }
  }, [preferencia]);

  // O tema do sistema é acompanhado sempre; só pesa quando a preferência é 'sistema'.
  useEffect(() => {
    const mediaQuery = consultaEscuro();
    if (!mediaQuery?.addEventListener) return undefined;
    const handleChange = (event) => setSistema(event.matches ? 'dark' : 'light');
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const setPreferencia = useCallback((valor) => {
    if (PREFERENCIAS_TEMA.includes(valor)) setPreferenciaEstado(valor);
  }, []);

  // Compatível com o setState de antes: aceita valor ou função do tema atual.
  const setTheme = useCallback((valor) => {
    setPreferenciaEstado((anterior) => {
      const atual = anterior === 'sistema' ? temaDoSistema() : anterior;
      const novo = typeof valor === 'function' ? valor(atual) : valor;
      return PREFERENCIAS_TEMA.includes(novo) ? novo : anterior;
    });
  }, []);

  const value = useMemo(() => ({
    theme,
    preferencia,
    isDarkMode: theme === 'dark',
    setTheme,
    setPreferencia,
    toggleTheme: () => setPreferenciaEstado(theme === 'dark' ? 'light' : 'dark')
  }), [theme, preferencia, setTheme, setPreferencia]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};
